import { useFormContext } from '@calendly/form'
import { nanoid } from 'nanoid'

import { useETEditorFormikPoolingType } from 'services/event_types'

import { findUserPool, getDefaultUserPoolName, updateMemberships } from '../helpers'
import { useSetValuesAndSubmit } from './use_set_values_and_submit'
import { useUserPoolsData } from './use_user_pools_data'
import { useAddMissingEventTypeMemberships } from '../../availability/utils'

export const useAddCollectiveHosts = (eventType) => {
  const isNewEventType = !eventType?.id
  const { isTeam } = eventType

  const { values: { memberships, allocationPreference } } = useFormContext()
  const setValues = useSetValuesAndSubmit({ withSubmit: !isNewEventType })
  const addMissingEventTypeMemberships = useAddMissingEventTypeMemberships()

  const { userPools, collectiveUserPools } = useUserPoolsData()
  const { isCollective, isRoundRobin, isCollectiveRoundRobin } = useETEditorFormikPoolingType()

  return (eventTypeMemberships, userPoolUid) => {
    // during creation when we add a member we need to add availability rules for newly added
    // no need to do it during edit since those will come from the back-end after section save
    const eventTypeMembershipsAttributes = isNewEventType ? {
      event_type_memberships_attributes: addMissingEventTypeMemberships(eventTypeMemberships)
    } : {}

    if (isCollective) {
      // When Collective
      // Then we update existing memberships
      return setValues((values) => ({
        ...values,
        memberships: updateMemberships(memberships, eventTypeMemberships, {
          markForDestruction: !isTeam,
        }),
        ...eventTypeMembershipsAttributes
      }))
    }

    if (isRoundRobin) {
      // When Round Robin
      // Then we convert it to Collective Round Robin
      const collectivePoolKey = nanoid(5)
      const roundRobinPoolKey = nanoid(5)

      const updatedRoundRobinPoolMemberships = updateMemberships([], memberships, {
        userPoolUid: roundRobinPoolKey,
        addPriority: true,
        markForDestruction: !isTeam,
      })

      // Merge existing memberships (with RR user pool UID) and the new memberships for the collective pool
      const updatedMemberships = updateMemberships(updatedRoundRobinPoolMemberships, eventTypeMemberships, {
        userPoolUid: collectivePoolKey,
        markForDestruction: !isTeam,
      })

      const hasNoNewMemberships = !updatedMemberships.some((membership) => membership.user_pool_uid === collectivePoolKey)
      if (hasNoNewMemberships) { return null }

      return setValues((values) => ({
        ...values,
        allocationPreference: null,
        pooling_type: 'multi_pool',
        userPools: [
          {
            key: collectivePoolKey,
            allocation_preference: 'availability',
            pooling_type: 'collective',
            position: 0,
          },
          {
            key: roundRobinPoolKey,
            allocation_preference: allocationPreference || 'availability',
            pooling_type: 'round_robin',
            name: getDefaultUserPoolName(),
            position: 1,
          }
        ],
        memberships: updatedMemberships,
        ...eventTypeMembershipsAttributes
      }))
    }

    if (!isCollectiveRoundRobin) return null

    const collectiveUserPool = findUserPool(collectiveUserPools, userPoolUid) || collectiveUserPools[0]

    // When Collective Round Robin has a collective user pool,
    // Then we update memberships in it
    if (collectiveUserPool) {
      const updatedMemberships = updateMemberships(memberships, eventTypeMemberships, {
        userPoolUid,
        markForDestruction: !isTeam,
      })

      return setValues((values) => ({
        ...values,
        memberships: updatedMemberships,
        ...eventTypeMembershipsAttributes
      }))
    }

    // When Collective Round Robin ET does not have a collective user pool,
    // Then we add a collective user pool with hosts
    const collectivePoolKey = nanoid(5)

    const updatedMemberships = updateMemberships(memberships, eventTypeMemberships, {
      userPoolUid: collectivePoolKey,
      markForDestruction: !isTeam,
    })

    const hasNoNewMemberships = !updatedMemberships.some((membership) => membership.user_pool_uid === collectivePoolKey)
    if (hasNoNewMemberships) { return null }

    return setValues((values) => ({
      ...values,
      userPools: [
        ...userPools,
        {
          key: collectivePoolKey,
          allocation_preference: 'availability',
          pooling_type: 'collective',
          position: userPools.length,
        }
      ],
      memberships: updatedMemberships,
      ...eventTypeMembershipsAttributes
    }))
  }
}
